import React, { useState } from 'react';
import { toast } from 'sonner';

import { Modal } from '@/components/Modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useValidateRecoveryCodeMFA } from '@/hooks/useRecoveryCodes';
import { useAuth } from '@/state/auth';

interface EnterRecoveryCodeModalProps {
  open: boolean;
  onClose: () => void;
}

const CODE_LEN = 16;

export const EnterRecoveryCodeModal: React.FC<EnterRecoveryCodeModalProps> = ({
  open,
  onClose,
}) => {
  const [recoveryCode, setRecoveryCode] = useState('');
  const { credentials, login } = useAuth();

  const { mutateAsync: validateCode, status: validateStatus } =
    useValidateRecoveryCodeMFA();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Remove spaces and convert to uppercase, limit to CODE_LEN characters
    const value = e.target.value.replace(/\s/g, '').slice(0, CODE_LEN);
    setRecoveryCode(value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && recoveryCode.length === CODE_LEN) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    if (recoveryCode.length !== CODE_LEN) {
      toast.error(`Please enter a valid ${CODE_LEN}-character recovery code`);
      return;
    }

    if (!credentials.username) {
      toast.error('Username not available. Please try logging in again.');
      return;
    }

    try {
      await validateCode({
        username: credentials.username,
        code: recoveryCode,
      });

      login(credentials.username, credentials.password);
    } catch (error) {
      console.error('Recovery code validation failed:', error);
      // Error toast is already shown by the hook
    }
  };

  const handleClose = () => {
    setRecoveryCode('');
    onClose();
  };

  const isLoading = validateStatus === 'pending';

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Enter Recovery Code"
      size="xl"
      hideFooter
    >
      <div className="flex flex-col gap-6">
        {/* Header Description */}
        <div className="flex flex-col gap-2">
          <p className="text-chariot-text-secondary text-sm">
            Enter one of your recovery codes to access your account.
          </p>
          <div className="bg-chariot-background-secondary flex items-start gap-2 rounded-md p-3">
            <div className="text-chariot-brand-primary mt-[-2px]">ℹ️</div>
            <p className="text-chariot-text-secondary text-xs">
              Each recovery code can only be used once and will be deactivated
              after use.
            </p>
          </div>
        </div>

        {/* Recovery Code Input */}
        <div className="flex flex-col gap-2">
          <label className="text-chariot-text-primary text-sm font-semibold">
            Recovery Code
          </label>
          <Input
            type="text"
            value={recoveryCode}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Aa1Bb2Cc3Dd4Ee5F"
            className="font-mono text-lg tracking-widest"
            maxLength={CODE_LEN}
            autoComplete="off"
            autoFocus
            disabled={isLoading}
          />
          <p className="text-chariot-text-secondary text-xs">
            Enter the {CODE_LEN}-character code without spaces
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <Button
            onClick={handleClose}
            variant="outline"
            className="flex-1"
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            className="flex-1"
            disabled={recoveryCode.length !== CODE_LEN || isLoading}
          >
            {isLoading ? 'Validating...' : 'Use Recovery Code'}
          </Button>
        </div>

        {/* Help Text */}
        <div className="border-chariot-stroke bg-chariot-background-secondary mb-2 rounded-md border p-3">
          <p className="text-chariot-text-secondary text-xs">
            <strong>Can&apos;t find your recovery codes?</strong> You may have
            saved them when you first set up MFA. Check your password manager,
            secure notes, or contact support for assistance.
          </p>
        </div>
      </div>
    </Modal>
  );
};
